// src/pages/B2B/event-reports/csv/buildCsv.ts
import type { EventReportPayload, BusinessRow } from '../types';

export interface BuildCsvInput {
  payload: EventReportPayload;
  eventName: string;
  fromLocal: string;
  toLocal: string;
  timezone: string;
  generatedAt: string;
  generatedBy: string;
}

const BUSINESS_COLUMNS: Array<keyof BusinessRow> = [
  'business_id',
  'business_name',
  'check_ins',
  'distinct_check_in_users',
  'going_intents',
  'live_started',
  'live_completed',
  'distinct_live_users',
  'avg_live_minutes',
  'max_live_minutes',
  'favorites',
  'events_held',
  'event_interests_going',
  'event_interests_interested',
  'deals_created',
  'deal_views',
  'distinct_deal_viewers',
  'deal_views_business_page',
  'deal_views_notification',
  'business_notifications_sent',
  'business_notifications_delivered',
  'business_notifications_opened',
  'business_notifications_open_rate',
];

function escape(v: unknown): string {
  if (v === null || v === undefined) return '';
  let s = String(v);
  // Venue and event names are free text; a leading = + - @ would run as a
  // formula when the file is opened in a spreadsheet.
  if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/["',\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function row(values: unknown[]): string {
  return values.map(escape).join(',');
}

export function buildCsv(input: BuildCsvInput): string {
  const { payload, eventName, fromLocal, toLocal, timezone, generatedAt, generatedBy } = input;

  const lines: string[] = [];
  lines.push('# Event Report');
  lines.push(`# Event Name: ${eventName.replace(/[\r\n]+/g, ' ')}`);
  lines.push(`# From: ${fromLocal} ${timezone}`);
  lines.push(`# To: ${toLocal} ${timezone}`);
  lines.push(`# Generated At: ${generatedAt}`);
  lines.push(`# Generated By: ${generatedBy}`);
  lines.push('#');
  lines.push('# Summary');
  lines.push(`# total_new_signups, ${payload.signups.total}`);
  lines.push(`# area_notifications_sent, ${payload.area_notifications.sent}`);
  lines.push(`# area_notifications_audience, ${payload.area_notifications.audience}`);
  lines.push(`# area_notifications_delivered, ${payload.area_notifications.delivered}`);
  lines.push(`# area_notifications_opened, ${payload.area_notifications.opened}`);
  lines.push(`# area_notifications_open_rate, ${payload.area_notifications.open_rate.toFixed(4)}`);
  lines.push('');

  lines.push('[BUSINESSES]');
  lines.push(BUSINESS_COLUMNS.join(','));
  for (const b of payload.businesses) {
    lines.push(row(BUSINESS_COLUMNS.map((c) => b[c] ?? '')));
  }
  lines.push('');

  lines.push('[USERS_DAILY]');
  lines.push('date,new_signups_total,email,google,apple');
  for (const d of payload.signups.daily) {
    lines.push(row([d.date, d.total, d.email, d.google, d.apple]));
  }

  if (payload.spotlight_venue) {
    const sv = payload.spotlight_venue;
    lines.push('');
    lines.push('[SPOTLIGHT_VENUE]');
    lines.push('business_id,business_name,check_ins,deal_views,going_clicks,live_sessions');
    lines.push(
      row([sv.id, sv.name, sv.check_ins, sv.deal_views, sv.going_clicks, sv.live_sessions]),
    );
  }

  return lines.join('\n');
}
